import { useEffect, useMemo } from "react";
import { useController, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import Select from "../../../components/Form/Select";
import { useTranslationApp } from "../../../lib/i18next";
import {
  buildFontSelection,
  findFamily,
  getDefaultFace,
  listSelectableFaces,
  resolveLayerFont,
} from "../utils/fonts";
import { TEMPLATES_FONT_FACE } from "../../../i18n/keysTranslations";
import { useMergedFonts } from "./FontFamilySelect";

const optionalRules = { required: false };

const FontPreviewLabel = ({ familyName, fontWeight, italic, children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: `"${familyName}"`,
      fontWeight: fontWeight || 400,
      fontStyle: italic ? "italic" : "normal",
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 1.2,
      // Optical vertical centering in filled Select value/options.
      transform: "translateY(2px)",
    }}
  >
    {children}
  </Box>
);

const FontFaceSelect = ({
  control,
  errors,
  disabled = false,
  name = "fontFaceRef",
  fonts: fontsFromParent,
  onChangeField,
}) => {
  const { t } = useTranslationApp();
  const ownsListener = fontsFromParent === undefined;
  const listenedFonts = useMergedFonts({ enabled: ownsListener });
  const families = useMemo(
    () => (ownsListener ? listenedFonts : fontsFromParent || []),
    [ownsListener, listenedFonts, fontsFromParent]
  );
  const fontAssetRef = useWatch({ control, name: "fontAssetRef" }) || "";
  const fontFaceRef = useWatch({ control, name }) || "";
  const fontWeight = useWatch({ control, name: "fontWeight" });
  const { field } = useController({
    control,
    name,
    rules: optionalRules,
  });

  const resolved = useMemo(
    () =>
      resolveLayerFont({
        families,
        fontAssetRef,
        fontFaceRef,
        fontWeight,
      }),
    [families, fontAssetRef, fontFaceRef, fontWeight]
  );
  const family =
    findFamily({ families, fontAssetRef: resolved?.familyName || fontAssetRef }) ||
    findFamily({ families, fontAssetRef });
  const faces = listSelectableFaces(family);
  const selectedFaceKey = resolved?.faceKey || fontFaceRef || getDefaultFace(family)?.key || "";

  useEffect(() => {
    if (!fontFaceRef && selectedFaceKey) {
      field.onChange(selectedFaceKey);
    }
  }, [field, fontFaceRef, selectedFaceKey]);

  const options = useMemo(
    () =>
      faces.map((face) => ({
        value: face.key,
        label: (
          <FontPreviewLabel
            familyName={family?.familyName || fontAssetRef}
            fontWeight={face.weight}
            italic={face.italic}
          >
            {face.name}
          </FontPreviewLabel>
        ),
      })),
    [faces, family, fontAssetRef]
  );

  if (!family || !options.length) return null;

  return (
    <Select
      control={control}
      errors={errors}
      name={name}
      label={t(TEMPLATES_FONT_FACE)}
      variant="filled"
      fullWidth
      displayEmpty
      disabled={disabled}
      rules={optionalRules}
      sx={{
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
        },
      }}
      options={
        selectedFaceKey && !options.some((option) => option.value === selectedFaceKey)
          ? [
              {
                value: selectedFaceKey,
                label: (
                  <FontPreviewLabel
                    familyName={family.familyName}
                    fontWeight={resolved?.weight}
                    italic={resolved?.italic}
                  >
                    {resolved?.name || selectedFaceKey}
                  </FontPreviewLabel>
                ),
              },
              ...options,
            ]
          : options
      }
      renderValue={(selected) => {
        const face =
          faces.find((item) => item.key === selected) ||
          faces.find((item) => item.key === selectedFaceKey);
        if (!face) return selected || "";
        return (
          <FontPreviewLabel
            familyName={family.familyName}
            fontWeight={face.weight}
            italic={face.italic}
          >
            {face.name}
          </FontPreviewLabel>
        );
      }}
      actionsChange={(value) => {
        const face =
          faces.find((item) => item.key === value) || getDefaultFace(family);
        const selection = buildFontSelection({ family, face });
        field.onChange(selection.fontFaceRef);
        onChangeField?.({ key: "fontSelection", value: selection });
      }}
    />
  );
};

export default FontFaceSelect;
